"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { motion } from "framer-motion";
import { 
  Mail, 
  ArrowRight, 
  Loader2, 
  AlertCircle,
  Sparkles 
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();

  // Redirecionar se já estiver logado
  useEffect(() => {
    if (!authLoading && user) {
      router.push("/");
    }
  }, [user, authLoading, router]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Chamar API de login simplificado
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Error al iniciar sesión");
      }

      // Verificar o token usando Supabase para criar a sessão
      if (data.tokenHash) {
        let verifySuccess = false;
        let lastError = null;

        // Tentar até 3 vezes com pequeno delay entre tentativas
        for (let attempt = 1; attempt <= 3; attempt++) {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: data.tokenHash,
            type: "magiclink",
          });

          if (!verifyError) {
            verifySuccess = true;
            break;
          }

          console.log(`Verify attempt ${attempt} failed:`, verifyError.message);
          lastError = verifyError;

          // Aguardar um pouco antes de tentar novamente (exceto na última tentativa)
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 500 * attempt));
          }
        }

        if (!verifySuccess) {
          console.error("All verify attempts failed:", lastError);
          throw new Error("Tu email es correcto, pero hubo un problema de conexión. Por favor, intenta nuevamente en unos segundos.");
        }

        // Sessão criada com sucesso - redirecionar para home
        window.location.href = "/";
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Error al iniciar sesión. Intente nuevamente.");
      setLoading(false);
    }
  };

  // Mostrar loading enquanto verifica auth
  if (authLoading) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-[1000]">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  // Não mostrar nada se já estiver logado (vai redirecionar)
  if (user) {
    return null;
  }

  return (
    <div className="fixed inset-0 overflow-x-hidden z-[1000] w-screen min-h-screen flex items-center justify-center bg-gray-50 px-4 py-5">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <motion.form
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          onSubmit={handleSubmit}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="pt-5 pb-3 px-6 text-center">
              <div className="flex justify-center mb-3">
                <Image
                  src="/images/moneyclub.webp"
                  alt="MoneyClub"
                  height={70}
                  width={140}
                  priority
                />
              </div>
              
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-900 font-semibold">Acceso Exclusivo</span>
              </div>
              
              <p className="text-gray-500 text-sm">
                Utiliza el email con el que realizaste la compra
              </p>
            </div>

            {/* Form Content */}
            <div className="px-6 pb-6 space-y-4">
              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-gray-700 text-sm font-medium">
                  Correo electrónico
                </label>
                <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:bg-white transition-all">
                  <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    required
                    className="w-full py-3.5 bg-transparent outline-none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
                >
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-sm">{error}</p>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={loading || !email}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Ingresando...
                  </>
                ) : (
                  <>
                    Ingresar
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>
          </div>

          {/* Footer */}
          <p className="mt-4 text-xs text-gray-400 text-center">
            Acceso exclusivo para miembros.
            <br />
            Sin contraseña necesaria.
          </p>
        </motion.form>
      </motion.div>
    </div>
  );
}
